import { IDropdownOption } from '@ui-modules/types';

export enum MultiSteps {
  ApprovingSharedDrive = 'approving_shared_drive',
  MainInfo = 'main_info',
  ReturnTrip = 'return_trip',
  TripInfo = 'trip_info',
}

// Common
export interface IChoice {
  display_name: string;
  value: string;
}

// Drive
export interface DriveFormData {
  agency?: string;
  addRecurring?: boolean;
  currentDate: string | Date;
  driver?: IDropdownOption;
  driver_return?: IDropdownOption;
  dropoff_date: Date;
  dropoff_date_return?: Date;
  dropoff_location: string;
  dropoff_location_return?: string;
  dropoff_time: Date;
  dropoff_time_return?: Date;
  dropoff_town: string;
  dropoff_town_return?: string;
  email?: string;
  first_name?: string;
  uuid?: string;
  index_number?: string;
  last_name?: string;
  manager_email?: string;
  n_of_passengers?: IDropdownOption;
  no_passengers?: boolean;
  phone_number?: string;
  pickup_date: Date;
  pickup_date_return?: Date;
  pickup_location: string;
  pickup_location_return?: string;
  pickup_time: Date;
  pickup_time_return?: Date;
  pickup_town: string;
  pickup_town_return?: string;
  purpose?: IDropdownOption;
  recurring_period?: IDropdownOption;
  recurring_until?: Date;
  recurring_frequency?: IDropdownOption;
  remarks?: string;
  requesting_unit?: IDropdownOption;
  transfer_type: 'airport' | 'in_town' | 'out_of_town';
  type_of_trip?: IDropdownOption;
  vehicle: IDropdownOption;
  vehicle_return?: IDropdownOption;
  drives: DriveInfo[];
}

export interface DriverBookingDetail {
  transfer_type: 'airport' | 'in_town' | 'out_of_town';
  type_of_trip: 'one-way' | 'round-trip' | 'multi-leg';
}

export interface DriverFacilityBookingDefault {
  driver_detail: DriverBookingDetail;
  facility: number;
}

export interface DriverFacilityBooking extends DriverFacilityBookingDefault {
  agency: string;
  agency_fk?: string;
  email: string;
  first_name: string;
  uuid?: string;
  id?: string;
  is_third_party: boolean;
  index_number: string;
  last_name: string;
  manager_email: string;
  n_of_passengers?: number;
  phone_number: string;
  purpose: string;
  remarks: string | null;
  requesting_unit: string;
  transfer_type: string;
  type_of_trip: string;
  type_of_trip_display: string;
  transfer_type_display: string;
  requesting_unit_display: string;
}

export interface Drive {
  driver_agency: string;
  driver_email: string;
  driver_first_name: string;
  driver_id: string;
  driver_last_name: string;
  driver_phone_number: string;
  dropoff_date: string;
  dropoff_location: string;
  dropoff_time: string;
  dropoff_town: string;
  editable_state: string;
  focal_point_first_name?: string;
  focal_point_last_name?: string;
  uuid?: string;
  passenger_agency: string;
  passenger_email: string;
  passenger_first_name: string;
  passenger_last_name: string;
  passenger_number_of_pax: string;
  passenger_phone_number: string;
  passenger_remarks: string | null;
  pickup_date: string;
  pickup_location: string;
  pickup_time: string;
  pickup_town: string;
  vehicle_agency: string;
  vehicle_id: string;
}

export interface MappedDrive extends Drive {
  id?: string;
  booking: number | string | null;
  driver: string | null;
  end_date: Date;
  is_hide: boolean;
  is_temporary?: boolean;
  n_of_passengers?: string;
  start_date: Date;
  event_id: string;
}

export interface DrivePatch {
  booking: Partial<DriverFacilityBooking> | number; // TO DO: some properties are required!
  driver?: string | null;
  dropoff_date?: string;
  dropoff_location?: string;
  dropoff_time?: string | null;
  dropoff_town?: string;
  has_passengers?: boolean;
  uuid?: string;
  is_temporary?: boolean;
  pickup_date?: string;
  pickup_location?: string;
  pickup_time?: string;
  pickup_town?: string;
  vehicle?: string | null;
  price_category: string;
  booked_route_order?: number;
}

export interface PlannerFormData {
  is_booking_on_behalf_of_colleague: boolean;
  has_passengers: boolean;
  agent_details?: {
    booking_agent_username: string;
    booking_agent_last_name: string;
    booking_agent_first_name: string;
    booking_agent_email: string;
  };
  booking_details?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    agency?: string;
    phone_number?: string;
    index_number?: string;
  };
  driver_detail: {
    transfer_type: string;
    type_of_trip: string;
    passenger_infos?: PassengerInfo[];
  };
  booking_extra: {
    manager_email?: string;
    purpose?: string;
    remarks?: string;
    flight_number?: string;
    flight_arrival_departure_time?: string;
    requesting_unit?: string;
    n_of_passengers?: number;
  };
  drives: DriveInfo[];
  recurring_option: {
    uuid?: string;
    start_date: string;
    end_date: string;
    frequency: string;
    weekdays: number[];
  };
  force_create?: boolean;
}

export interface DriveInfo {
  vehicle: IDropdownOption;
  driver: IDropdownOption;
  pickup_date: Date;
  pickup_time: Date;
  pickup_location: string;
  pickup_town: string;
  dropoff_date: Date;
  dropoff_time: Date;
  dropoff_location: string;
  dropoff_town: string;
}

export interface PassengerInfo {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  requsting_unit: string[];
  agency: string;
}

export interface SavedDrive {
  booking: string;
  driver: string;
  dropoff_date: string;
  dropoff_location: string;
  dropoff_time: string;
  dropoff_town: string;
  uuid: string;
  is_temporary: boolean;
  pickup_date: string;
  pickup_location: string;
  pickup_time: string;
  pickup_town: string;
  price_category: 'short' | 'medium' | 'long' | 'generic';
  vehicle: string;
}

export interface AcceptingDrive {
  booking: string | null;
  driver: string | null;
  dropoff_date: string;
  dropoff_location: string;
  dropoff_time: string | null;
  dropoff_town: string;
  editable_state: string;
  end_date: Date; // TO DO: Should be delete
  uuid?: number;
  is_hide: boolean;
  is_temporary: boolean;
  pickup_date: string;
  pickup_location: string;
  pickup_time: string;
  pickup_town: string;
  price_category: string;
  start_date: Date; // TO DO: Should be delete
  vehicle: string | null;
  invalid?: boolean;
}

export interface PostAcceptingDrive {
  booking: string | null;
  drives: {
    vehicle: string | null;
    driver: string | null;
    pickup_date: string;
    pickup_time: string;
    pickup_location: string;
    pickup_town: string;
    dropoff_date: string;
    dropoff_time: string | null;
    dropoff_location: string;
    dropoff_town: string;
  }[];
}

export interface AcceptingDriveData {
  accepted: boolean;
  booking: number | null;
  driver: number | null;
  driver_agency: string | null;
  driver_email: string | null;
  driver_first_name: string | null;
  driver_id?: number | null;
  driver_last_name: string | null;
  driver_phone_number: string | null;
  dropoff_date: string;
  dropoff_location: string;
  dropoff_time: string;
  dropoff_town: string;
  editable_state?: string;
  end_date: Date;
  focal_point_first_name: string;
  focal_point_last_name: string;
  uuid?: number;
  is_hide?: boolean;
  is_temporary?: boolean;
  invalid?: boolean;
  passenger_agency?: string;
  passenger_email: string;
  passenger_first_name: string;
  passenger_last_name: string;
  passenger_number_of_pax: string;
  passenger_phone_number: string;
  passenger_remarks: string;
  pickup_date: string;
  pickup_location: string;
  pickup_time: string;
  pickup_town: string;
  price_category?: string;
  start_date: Date;
  vehicle: number | null;
  vehicle_id: number | null;
}

export interface DrivesForm {
  driver: string;
  dropoff_date: string;
  dropoff_location: string;
  dropoff_time: string | null;
  dropoff_town: string;
  pickup_date: string;
  pickup_location: string;
  pickup_time: string;
  pickup_town: string;
  vehicle: string;
}

export interface AcceptingFormData {
  drives: DrivesForm[];
  force_create?: boolean;
}

export interface FacilityDriver {
  driver_agency: string;
  driver_identifier: string;
  email: string;
  last_name: string;
  name: string;
  phone_number: string;
  shared: boolean;
}

export interface FacilityDriverMapped {
  driverAgency: string;
  driverId: string;
  email: string;
  lastName: string;
  name: string;
  phoneNumber: string;
  shared: boolean;
}

export interface FacilityVehicle {
  agency_logo: string | null;
  image: string;
  is_disabled?: boolean;
  key?: string;
  label: string;
  max_capacity: number;
  plate_number: string;
  preferred_driver: string | null;
  protection: string;
  shared: boolean;
  shared_agency: Array<number>;
  vehicle_agency: string | null;
  vehicle_identifier: string;
}

export interface FacilityVehicleMapped {
  agencyLogo: string | null;
  image: string;
  isDisabled?: boolean;
  label: string;
  maxCapacity: number;
  plateNumber: string;
  preferredDriver: string | null;
  protection: string;
  shared: boolean;
  sharedAgency: Array<number>;
  vehicleAgency: string | null;
  vehicleId: string;
}

export interface IFormDriveEdit {
  driver: IDropdownOption;
  dropoffDate: Date;
  dropoffTime: Date;
  pickupDate: Date;
  pickupTime: Date;
  vehicle: IDropdownOption;
}

export interface IUpdateDrive {
  driver?: string;
  dropoff_date?: string;
  dropoff_location?: string;
  dropoff_time?: string;
  dropoff_town?: string;
  is_active: boolean;
  pickup_date?: string;
  pickup_location?: string;
  pickup_time?: string;
  pickup_town?: string;
  recurring?: string;
  route?: string;
  uuid: string;
  vehicle?: string;
}
